import { FC } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Stack } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Edit, Hide, Move, Reveal } from "@/icons/outlined";

interface FundRowProps {
  id: string;
  name: string;
  code?: string | null;
  formCount: number;
  isVisible: boolean;
  onEdit: () => void;
  onToggleVisibility: () => void;
}

export const FundRow: FC<FundRowProps> = ({ id, name, code, formCount, isVisible, onEdit, onToggleVisibility }) => {
  const { t } = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isVisible,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: isVisible ? 0.5 : 2,
        pr: 2,
        py: 1.5,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        borderRadius: 1,
        backgroundColor: theme.palette.surface.neutral.white,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? "grabbing" : "default",
      })}
    >
      {isVisible && (
        <DashboardIconButton
          variant="text"
          size="small"
          vibe="neutral"
          sx={{ cursor: "grab", touchAction: "none" }}
          {...attributes}
          {...listeners}
        >
          <Move />
        </DashboardIconButton>
      )}
      <Stack flex={1}>
        <DashboardTypography variant="subtitle2" vibe={isVisible ? "text-neutral-intense" : "text-neutral-supershy"}>
          {name}
        </DashboardTypography>

        <DashboardTypography variant="body2" vibe={isVisible ? "text-neutral-quiet" : "text-neutral-supershy"}>
          {[
            code,
            formCount > 0 && t("dashboard", "funds.manage.formsCount", { count: formCount }),
            !isVisible && t("dashboard", "funds.manage.hidden"),
          ]
            .filter(Boolean)
            .join(" • ")}
        </DashboardTypography>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <DashboardIconButton variant="text" size="small" vibe="neutral" onClick={onEdit}>
          <Edit />
        </DashboardIconButton>
        <DashboardIconButton variant="text" size="small" vibe="neutral" onClick={onToggleVisibility}>
          {isVisible ? <Reveal /> : <Hide />}
        </DashboardIconButton>
      </Stack>
    </Box>
  );
};
